<template>
  <div class="stage-item p-3 border-round border-1" :class="stageClass">
    <div class="flex justify-content-between align-items-center mb-2">
      <div class="flex align-items-center gap-2">
        <i :class="statusIcon" :style="{ color: statusColor }"></i>
        <span class="font-medium">{{ title }}</span>
      </div>
      <Badge v-if="normalizedStatus" :value="statusLabel" :severity="statusSeverity" />
    </div>

    <!-- Прогресс бар для активного этапа -->
    <div v-if="normalizedStatus === 'in_progress' && stageData.total > 0" class="mb-2">
      <ProgressBar :value="progressPercent" :showValue="false" style="height: 6px" />
      <div class="flex justify-content-between text-sm text-600 mt-1">
        <span>
          {{ stageData.processed || 0 }} / {{ stageData.total }}
          <span v-if="stageData.current_batch && stageData.total_batches" class="text-500">
            (батч {{ stageData.current_batch }}/{{ stageData.total_batches }})
          </span>
        </span>
        <span v-if="stageData.success !== undefined">
          Успешно: {{ stageData.success }} |
          <span v-if="stageData.no_changes !== undefined && stageData.no_changes > 0">Без изменений: {{ stageData.no_changes }} | </span>
          Ошибок: {{ stageData.failed || 0 }}
        </span>
        <span v-if="estimatedTimeRemaining" class="text-500 ml-2">
          ~ {{ estimatedTimeRemaining }}
        </span>
      </div>
    </div>

    <!-- Итоговая статистика завершённого этапа -->
    <div v-if="normalizedStatus === 'completed' && stageData.total > 0" class="text-sm text-600 mb-1">
      <span>Обработано: {{ stageData.processed || stageData.total }}</span>
      <span v-if="stageData.success !== undefined"> | Успешно: {{ stageData.success }}</span>
      <span v-if="stageData.no_changes > 0"> | Без изменений: {{ stageData.no_changes }}</span>
      <span v-if="stageData.failed > 0" class="text-red-600"> | Ошибок: {{ stageData.failed }}</span>
    </div>

    <!-- Сообщение -->
    <p v-if="stageData?.message" class="text-sm text-600 mb-0">
      {{ stageData.message }}
    </p>

    <!-- Время выполнения -->
    <div v-if="stageData?.completed_at && stageData?.started_at" class="text-xs text-500 mt-2">
      <i class="pi pi-clock"></i>
      Выполнено за {{ executionTime }}
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import ProgressBar from 'primevue/progressbar'
import Badge from 'primevue/badge'
import { parseUtc, formatDuration } from '@/utils/dateHelpers'
import { useEtaTimer } from '@/composables/useEtaTimer'

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  stageData: {
    type: Object,
    default: null
  },
  stageName: {
    type: String,
    required: true
  }
})

const normalizedStatus = computed(() => {
  const status = props.stageData?.status
  if (!status) return null
  const aliases = {
    running: 'in_progress'
  }
  return aliases[status] || status
})

const progressPercent = computed(() => {
  if (!props.stageData || !props.stageData.total) return 0
  return Math.round((props.stageData.processed / props.stageData.total) * 100)
})

const statusIcon = computed(() => {
  if (!normalizedStatus.value) return 'pi pi-circle text-400'
  
  const icons = {
    'pending': 'pi pi-circle',
    'in_progress': 'pi pi-spin pi-spinner',
    'completed': 'pi pi-check-circle',
    'skipped': 'pi pi-minus-circle',
    'failed': 'pi pi-times-circle'
  }
  return icons[normalizedStatus.value] || 'pi pi-circle'
})

const statusColor = computed(() => {
  if (!normalizedStatus.value) return '#9e9e9e'
  
  const colors = {
    'pending': '#9e9e9e',
    'in_progress': '#2196F3',
    'completed': '#4caf50',
    'skipped': '#ff9800',
    'failed': '#f44336'
  }
  return colors[normalizedStatus.value] || '#9e9e9e'
})

const statusLabel = computed(() => {
  if (!normalizedStatus.value) return 'Ожидание'
  
  const labels = {
    'pending': 'Ожидание',
    'in_progress': 'В процессе',
    'completed': 'Завершено',
    'skipped': 'Пропущено',
    'failed': 'Ошибка'
  }
  return labels[normalizedStatus.value] || 'Неизвестно'
})

const statusSeverity = computed(() => {
  if (!normalizedStatus.value) return 'secondary'
  
  const severities = {
    'pending': 'secondary',
    'in_progress': 'info',
    'completed': 'success',
    'skipped': 'warn',
    'failed': 'danger'
  }
  return severities[normalizedStatus.value] || 'secondary'
})

const stageClass = computed(() => {
  if (!normalizedStatus.value) return 'border-300'
  
  const classes = {
    'pending': 'border-300',
    'in_progress': 'border-primary bg-primary-50',
    'completed': 'border-green-300 bg-green-50',
    'skipped': 'border-orange-300 bg-orange-50',
    'failed': 'border-red-300 bg-red-50'
  }
  return classes[normalizedStatus.value] || 'border-300'
})

// Реактивное текущее время для ETA (обновляется каждые 3 сек)
const { now } = useEtaTimer(3000)

const estimatedTimeRemaining = computed(() => {
  // Зависимость от now для реактивного обновления
  const _tick = now.value

  if (normalizedStatus.value !== 'in_progress') return null
  if (!props.stageData?.started_at || !props.stageData?.total) return null

  const processed = props.stageData.processed || 0
  if (processed <= 0) return null

  const startedAt = parseUtc(props.stageData.started_at)
  const elapsedSec = (Date.now() - startedAt.getTime()) / 1000
  if (elapsedSec <= 0) return null

  const speed = processed / elapsedSec
  const remaining = (props.stageData.total - processed) / speed

  if (remaining <= 0) return null
  return formatDuration(remaining)
})

const executionTime = computed(() => {
  if (!props.stageData?.completed_at || !props.stageData?.started_at) return ''
  
  const start = parseUtc(props.stageData.started_at)
  const end = parseUtc(props.stageData.completed_at)
  const diff = Math.round((end - start) / 1000) // в секундах
  
  if (diff < 60) return `${diff} сек`
  const minutes = Math.floor(diff / 60)
  const seconds = diff % 60
  return `${minutes} мин ${seconds} сек`
})
</script>

<style scoped>
.stage-item {
  transition: all 0.3s ease;
}
</style>
